import { trans } from '@lingoda/i18n';
import { useGoBack } from '@lingoda/router';
import {
    BackButtonBar,
    Box,
    FramePageContainer,
    Hidden,
    Typography,
    useResolution,
} from '@lingoda/ui';
import type { BackgroundThemeEnum } from '@lingoda/monolith-graphql';
import { getChallengeStatus, getChallengeStatusText } from '../../utils';
import { JoinButton } from '../JoinButton';
import { MobileBackgroundImageBox } from './MobileBackgroundImageBox';
import { ChallengeParticipationStatus } from './ChallengeParticipationStatus';
import { DesktopBackgroundImageStack } from './DesktopBackgroundImageStack';

interface TopSectionLayoutProps {
    startDate: string;
    endDate: string;
    joined: boolean;
    participants: number;
    title: string;
    subtitle: string;
    onJoinClick: () => void;
    joiningLoading: boolean;
    desktopImageUrl: string | undefined;
    mobileImageUrl: string | undefined;
    desktopBackgroundTheme: BackgroundThemeEnum;
    mobileBackgroundTheme: BackgroundThemeEnum;
}

export const TopSectionLayout = ({
    title,
    subtitle,
    startDate,
    endDate,
    joined,
    participants,
    onJoinClick,
    joiningLoading,
    desktopImageUrl,
    mobileImageUrl,
    desktopBackgroundTheme,
    mobileBackgroundTheme,
}: TopSectionLayoutProps) => {
    const { canGoBack, goBack } = useGoBack();

    const status = getChallengeStatus(startDate, endDate);
    const statusText = getChallengeStatusText(status, startDate, endDate);

    const isMobile = useResolution('phone');
    const theme = isMobile ? mobileBackgroundTheme : desktopBackgroundTheme;

    return (
        <Box position="relative">
            <Hidden smUp>
                <MobileBackgroundImageBox imageUrl={mobileImageUrl} />
            </Hidden>
            <Box pt={{ xs: 0, sm: 1 }} pb={{ xs: 0, sm: 1 }}>
                {canGoBack ? (
                    <FramePageContainer>
                        <BackButtonBar
                            goBack={goBack}
                            inverted={isMobile && desktopBackgroundTheme === 'dark'}
                        />
                    </FramePageContainer>
                ) : (
                    <Box mt={6} />
                )}
            </Box>
            <FramePageContainer
                px={{ xs: 2, sm: 3, md: 6 }}
                pt={{ xs: 28, smn: 32, sm: 0 }}
                maxWidth="smw"
            >
                <DesktopBackgroundImageStack desktopImageUrl={desktopImageUrl} themeMode={theme}>
                    <Typography
                        variant="subtitle1"
                        textTransform="uppercase"
                        sx={({ colors }) => ({
                            color: themed(theme, colors.text.subtle, colors.text.inverseSubtle),
                        })}
                    >
                        {subtitle}
                    </Typography>
                    <Typography variant="h1" maxWidth={{ sm: 370 }}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={({ colors }) => ({
                            color: themed(theme, colors.text.subtle, colors.text.inverseSubtle),
                        })}
                    >
                        {trans('banner-unlock-mystery-badges', {}, 'student-challenges')}
                    </Typography>
                    <ChallengeParticipationStatus
                        statusText={statusText}
                        participants={participants}
                    />
                    <JoinButton
                        onJoinClick={onJoinClick}
                        joined={joined}
                        joiningLoading={joiningLoading}
                        status={status}
                        theme={theme}
                    />
                </DesktopBackgroundImageStack>
            </FramePageContainer>
        </Box>
    );
};

function themed(theme: 'light' | 'dark', light: string, dark: string) {
    return theme === 'dark' ? dark : light;
}
